/* eslint-disable no-case-declarations */
/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Update, UpdateWithSideEffect } from 'use-reducer-with-side-effects';
import {
  ShopMatchingSetModule,
  ShopProductDiscoverSkusCollection,
  WaysToWearItCollection,
} from '../../../../types/definitions/vendor/graphql-schema.d';
import redirectToSignup from '../../../helpers/redirectToSignup';
import { updateStructuredMarkup } from '../../../helpers/structuredMarkup';
import onError from '../../../utils/onError';
import { sendCount } from '../../../utils/sendMetrics';
import {
  trackBuyNowClick,
  trackGtmBuyNowButtonClick,
} from '../../BuyNowButton/BuyNowButton.tracking';
import {
  trackFirstRelatedItemsScroll,
  trackRelatedItemClick,
  trackRelatedItemsViewed,
  trackRelatedItemsEndCardViewed,
  trackRelatedItemsEndCardClick,
  trackRelatedItemsViewAllClick,
  trackSimilarItemsLinkClick,
  trackWaysToWearItCarouselViewed,
  trackWaysToWearItOutfitItemClicked,
  trackWaysToWearItCarouselAccordionChanged,
  trackWaysToWearItOutfitViewed,
  trackInventoryAvailabilityEvent,
  trackNewEntryPointsCardView,
} from '../ProductDetailsPage.tracking';
import { ProductDetailsPageAction } from './ProductDetailsPageAction';
import { ProductDetailsPageState } from './ProductDetailsPageState';
import { ProductDetailsPageUnifiedState } from './ProductDetailsPageUnifiedState';
import useShopProductDetailsPageContextComputedState from './useShopProductDetailsPageContextComputedState';
import { GetShopRecommendedSizeQuery } from '../getShopRecommendedSize';

const hideCtaTooltips = { showBuyNow: false, showAddToBag: false };

// HACK: This is necessary to give side effects access to (now psudo) computed state
// This pattern has several downsides:
// * useShopProductDetailsPageContextComputedState state is relatively heavy
// * we are not able to use hooks within useShopProductDetailsPageContextComputedState
// * there are other packages that add side effects with access to computed state gracefully

const UpdateWithShopProductComputedState = (
  state: ProductDetailsPageState,
): ProductDetailsPageUnifiedState => {
  const computedState = useShopProductDetailsPageContextComputedState(state);

  return Update({ ...state, ...computedState });
};

// TODO: remove eslint disabling below once this function is being used
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UpdateWithShopProductComputedStateAndSideEffect = (
  state: ProductDetailsPageState,
  sideEffects: any,
) => {
  const computedState = useShopProductDetailsPageContextComputedState(state);

  return UpdateWithSideEffect({ ...state, ...computedState }, sideEffects);
};

/**
 * The reducer for ProductDetailsPageContext.
 *
 * See https://reactjs.org/docs/hooks-reference.html#usereducer for more information.
 *
 * @param state
 * @param action
 */
const productDetailsPageContextReducer = (
  oldState: ProductDetailsPageUnifiedState,
  action: ProductDetailsPageAction,
) => {
  if ((window as any).gon.env === 'development') {
    console.log(action.type);
  }

  const getShoppingBagBusinessLine = () => {
    try {
      const shoppingBag = window.localStorage?.getItem('ShoppingBag') || '{}';

      return JSON.parse(shoppingBag)?.businessLine;
    } catch (error) {
      onError(error);

      return null;
    }
  };

  let isKidsProductOrBusinessLine;

  switch (action.type) {
    case 'SET_SHOP_PRODUCT_QUERY_DATA':
      const shopProductData = action.payload.shopProductData || null;

      const shopProduct =
        shopProductData?.shopProductBySkuId ||
        shopProductData?.shopProductByExternalProductId ||
        null;

      /*
       * The selected variant is populated by the shopProductBySkuId
       * and is intended to be used when the client has selected a sku (size)
       * either via the Url parameter or via UI selection.
       */
      const selectedDefaultVariant =
        shopProductData?.shopProductBySkuId?.defaultVariant;
      const selectedVariantBySkuId =
        shopProductData?.shopProductBySkuId?.variantBySkuId;

      /*
       * The fallback variant is populated by the shopProductByExternalProductId
       * and is intended to be used when the client has not selected a sku (size)
       * either via the Url parameter or via UI selection.
       */
      const fallbackDefaultVariant =
        shopProductData?.shopProductByExternalProductId?.defaultVariant;
      const fallbackVariantBySkuId =
        shopProductData?.shopProductByExternalProductId?.variantBySkuId;

      /*
       * We have an edgecase where we want to auto select the size label
       * if there is only "one size" for a sku
       */
      let computedSelectedSizeLabel;

      const computedFallbackSizeLabel =
        fallbackVariantBySkuId?.size?.name ||
        fallbackDefaultVariant?.size?.name ||
        oldState.selectedSizeLabel ||
        null;

      if (
        computedFallbackSizeLabel?.toLocaleLowerCase()?.includes('one') &&
        computedFallbackSizeLabel?.toLocaleLowerCase()?.includes('size')
      ) {
        computedSelectedSizeLabel = computedFallbackSizeLabel;
      } else {
        computedSelectedSizeLabel =
          selectedVariantBySkuId?.size?.name ||
          selectedDefaultVariant?.size?.name ||
          oldState.selectedSizeLabel ||
          null;
      }

      const computedSelectedUniqueColorId =
        selectedVariantBySkuId?.color?.uniqueColorId ||
        selectedDefaultVariant?.color?.uniqueColorId ||
        oldState.selectedUniqueColorId ||
        null;

      const computedFallbackUniqueColorId =
        fallbackVariantBySkuId?.color?.uniqueColorId ||
        fallbackDefaultVariant?.color?.uniqueColorId ||
        oldState.fallbackUniqueColorId ||
        null;

      const nextUniqueColorId =
        computedSelectedUniqueColorId || computedFallbackUniqueColorId;

      let newSelectedUniqueColorIdHistory;

      if (nextUniqueColorId) {
        newSelectedUniqueColorIdHistory = [
          ...oldState.selectedUniqueColorIdHistory,
          ...[nextUniqueColorId],
        ];
      } else {
        newSelectedUniqueColorIdHistory = oldState.selectedUniqueColorIdHistory;
      }

      const shopProductShoppingBagBusinessLine = getShoppingBagBusinessLine();

      /*
       * Note: Some styles have variants which contain an inseam while others don't. An example is pants. Some regular pants may have inseam
       * while petite versions don't. This can cause a bug where we use the default variant inseam to query data for a petite pant without
       * any inseam and get inaccurate data back. The check below ensures we are using the correct inseam values belonging to the variant.
       */
      const setSelectedInseamLabel = () => {
        if (selectedVariantBySkuId?.sku) {
          return selectedVariantBySkuId?.inseam?.name || null;
        }

        if (selectedDefaultVariant?.sku) {
          return selectedDefaultVariant?.inseam?.name || null;
        }

        return oldState.selectedInseamLabel || null;
      };

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          shopProduct,
          selectedVariant:
            selectedVariantBySkuId ||
            selectedDefaultVariant ||
            oldState.selectedVariant ||
            null,
          fallbackVariant:
            fallbackVariantBySkuId ||
            fallbackDefaultVariant ||
            oldState.fallbackVariant ||
            null,
          selectedSkuId:
            selectedVariantBySkuId?.sku || selectedDefaultVariant?.sku || null,
          fallbackSkuId:
            fallbackVariantBySkuId?.sku || fallbackDefaultVariant?.sku || null,
          selectedUniqueColorId: computedSelectedUniqueColorId,
          selectedUniqueColorIdOnPageLoad:
            oldState?.selectedUniqueColorIdOnPageLoad ||
            computedSelectedUniqueColorId,
          fallbackUniqueColorId: computedFallbackUniqueColorId,
          selectedUniqueColorIdHistory: newSelectedUniqueColorIdHistory,
          // If someone removes the sku-id from the URL params, selectedVariantBySkuId
          // will be undefined. In this case, we want the selectedColorLabel to
          // match the fallback variant's color label
          selectedColorLabel: selectedVariantBySkuId
            ? selectedVariantBySkuId?.color?.name ||
              selectedDefaultVariant?.color?.name ||
              oldState.selectedColorLabel ||
              null
            : fallbackVariantBySkuId?.color?.name || null,
          fallbackColorLabel:
            fallbackVariantBySkuId?.color?.name ||
            fallbackDefaultVariant?.color?.name ||
            oldState.fallbackColorLabel ||
            null,
          selectedSizeLabel: computedSelectedSizeLabel,
          selectedSizeLabelOnPageLoad:
            oldState?.selectedSizeLabelOnPageLoad || computedSelectedSizeLabel,
          fallbackSizeLabel: computedFallbackSizeLabel,
          selectedInseamLabel: setSelectedInseamLabel(),
          selectedInseamLabelOnPageLoad:
            oldState?.selectedInseamLabelOnPageLoad || setSelectedInseamLabel(),
          fallbackInseamLabel:
            fallbackVariantBySkuId?.inseam?.name ||
            fallbackDefaultVariant?.inseam?.name ||
            oldState.selectedInseamLabel ||
            null,
          productBusinessLine: shopProduct?.businessLine || null,
          productDescription: shopProduct?.description,
          shoppingBagBusinessLine: shopProductShoppingBagBusinessLine,
          shoppingBagBusinessLineIsMismatched:
            !!shopProductShoppingBagBusinessLine &&
            shopProductShoppingBagBusinessLine !== shopProduct?.businessLine,
          loading: action.payload.shopProductLoading,
          error: action.payload.shopProductError,
        },
        (state: ProductDetailsPageUnifiedState) => {
          if (!state.loading && !state.error) {
            updateStructuredMarkup(state);

            if (state.title && oldState.title !== state.title) {
              document.title = state.title;
            }
          }

          if (state.error) {
            // Track the error
            onError(state.error);

            if (state.error.message.includes('408: Request Timeout')) {
              sendCount('shop-product-query.error.timeout');
              window.location.replace(`${window.location.pathname}/timeout`);
            } else if (
              state.error.message.includes('Sellable Product Variant not found')
            ) {
              sendCount('shop-product-query.error.sellable_variant_not_found');
              window.location.replace(`${window.location.pathname}`);
            } else {
              // AdsBot-Google user-agent is causing failed to fetch network errors and Korefak-Api has advised to ignore these
              if (!window.navigator?.userAgent?.includes('AdsBot-Google')) {
                sendCount('shop-product-query.error.fallback', {
                  error_message: state.error.message,
                });
              }
              window.location.replace(`${window.location.pathname}/not_found`);
            }
          }
        },
      );
    case 'SET_SHOP_PRODUCT_PRICE_AND_INVENTORY_QUERY_DATA':
      const selectedShopProductPriceAndInventory =
        action.payload.shopProductPriceAndInventoryData;

      const priceAndInventoryData =
        selectedShopProductPriceAndInventory?.shopProductBySkuId ||
        selectedShopProductPriceAndInventory?.shopProductByExternalProductId ||
        null;

      if (action.payload.shopProductPriceAndInventoryLoading) {
        return oldState;
      }

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          priceAndInventoryData: priceAndInventoryData || null,
          priceAndInventoryLoading: false,
          shopColorOptions: priceAndInventoryData?.colorOptions || [],
        },
        () => {
          return null;
        },
      );
    case 'SET_SHOP_PRODUCT_SELECTED_VARIANT':
      const selectedVariantProduct =
        action.payload.selectedVariantData?.shopProductBySkuId ||
        action.payload.selectedVariantData?.shopProductByExternalProductId;

      const selectedVariant = selectedVariantProduct?.variantBySelectedOptions;

      if (action.payload.selectedVariantLoading) {
        return oldState;
      }

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          selectedSkuId: selectedVariant?.sku || null,
          selectedVariant: selectedVariant || null,
          selectedVariantURL: selectedVariant?.url || null,
        },
        (state: ProductDetailsPageUnifiedState) => {
          if (!state.loading && !state.error) {
            updateStructuredMarkup(state);
          }
        },
      );
    case 'SET_SHOP_PRODUCT_FALLBACK_VARIANT':
      const fallbackVariantProduct =
        action.payload.fallbackVariantData?.shopProductBySkuId ||
        action.payload.fallbackVariantData?.shopProductByExternalProductId;

      const fallbackVariant = fallbackVariantProduct?.variantBySelectedOptions;

      if (action.payload.fallbackVariantLoading) {
        return oldState;
      }

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          fallbackSkuId: fallbackVariant?.sku || null,
          fallbackVariant: fallbackVariant || null,
          fallbackVariantURL: fallbackVariant?.url || null,
        },
        () => {
          return null;
        },
      );
    case 'SET_SHOP_PRODUCT_RECOMMENDED_COLOR_OPTIONS':
      const recommendedColorsProduct =
        action.payload.recommendedColorOptionsData?.shopProductBySkuId ||
        action.payload.recommendedColorOptionsData
          ?.shopProductByExternalProductId;

      const recommendedColors = recommendedColorsProduct?.colorOptions;

      if (action.payload.recommendedColorOptionsLoading) {
        return oldState;
      }

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          shopRecommendedColorOptionsBySize: recommendedColors || [],
          shopRecommendedColorOptionsBySizeLoading:
            action.payload.recommendedColorOptionsLoading,
        },
        () => {
          return null;
        },
      );
    case 'SET_SHOP_PRODUCT_PURCHASE_OPTIONS':
      if (action.payload.purchaseOptionsLoading) {
        return oldState;
      }

      const finalState = Update(
        {
          ...oldState,
          shopPurchaseOptions: action.payload.purchaseOptionsData,
          shopPurchaseOptionsLoading:
            action.payload.purchaseOptionsData === undefined,
        },
        () => null,
      );

      return finalState;

    case 'SET_SHOP_PRODUCT_COLOR_OPTIONS':
      const selectedColorsProduct =
        action.payload.selectedColorOptionsData?.shopProductBySkuId ||
        action.payload.selectedColorOptionsData?.shopProductByExternalProductId;

      const selectedColors = selectedColorsProduct?.colorOptions;

      if (action.payload.selectedColorOptionsLoading) {
        return oldState;
      }

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          shopColorOptionsBySize: selectedColors || [],
        },
        () => {
          return null;
        },
      );
    case 'SET_SHOP_PRODUCT_SIZE_OPTIONS':
      const selectedSizesProduct =
        action.payload.selectedSizeOptionsData?.shopProductBySkuId ||
        action.payload.selectedSizeOptionsData?.shopProductByExternalProductId;

      const selectedSizes = selectedSizesProduct?.sizeOptions;

      if (action.payload.selectedSizeOptionsLoading) {
        return oldState;
      }

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          shopSizeOptions: selectedSizes || [],
        },
        () => {
          return null;
        },
      );
    case 'SET_SHOP_PRODUCT_INSEAM_OPTIONS':
      const selectedInseamsProduct =
        action.payload.selectedInseamOptionsData?.shopProductBySkuId ||
        action.payload.selectedInseamOptionsData
          ?.shopProductByExternalProductId;

      const selectedInseams = selectedInseamsProduct?.inseamOptions;

      if (action.payload.selectedInseamOptionsLoading) {
        return oldState;
      }

      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          shopInseamOptions: selectedInseams || [],
        },
        () => {
          return null;
        },
      );
    case 'SET_CLIENT_DATA':
      const clientShopProduct = action.payload.clientData?.client;

      isKidsProductOrBusinessLine =
        action.payload.productBusinessLine === 'KIDS' ||
        clientShopProduct?.businessLine === 'KIDS';

      return Update(
        {
          ...oldState,
          clientBusinessLine: clientShopProduct?.businessLine || null,
          clientNeedsOnboarding:
            !!clientShopProduct?.capabilities?.needsOnboarding,
          // HACK: When a client is logged out, client is null from graphql's response
          clientIsLoggedIn: !!clientShopProduct?.id,
          clientName: clientShopProduct?.firstName,
          clientHasPurchases: clientShopProduct?.hasPurchases || false,
          clientBusinessLineIsMismatched:
            !!clientShopProduct?.businessLine &&
            clientShopProduct?.businessLine !==
              action.payload.productBusinessLine &&
            // HACK: Don't show mismatched business line error on Kids PDPs or for Kids accounts viewing PDPS of other business lines
            !isKidsProductOrBusinessLine,
          clientLoading: action.payload.clientLoading,
          isAuthenticatedClient: clientShopProduct?.isAuthenticatedClient,
        },
        () => {
          return null;
        },
      );
    case 'SET_FEATURE_FLAGS':
      const clientFeatureFlags = action.payload.featureFlags;

      return Update({
        ...oldState,
        clientFeatureFlags,
      });
    case 'SET_VISITOR_FEATURE_FLAGS':
      const visitorFeatureFlags = action.payload.featureFlags;

      return Update({
        ...oldState,
        visitorFeatureFlags,
      });

    case 'SET_SHOP_RECOMMENDED_SIZES_QUERY_DATA':
      let recommendedSizesArr: GetShopRecommendedSizeQuery[] = [];
      const recommendedSizes = action.payload.recommendedSizes;

      const recommendedSkuSizeLabel =
        recommendedSizes?.shopProductBySkuId?.variantBySkuId?.size?.name ||
        null;
      const recommendedSkuInseamLabel =
        recommendedSizes?.shopProductBySkuId?.variantBySkuId?.inseam?.name ||
        null;

      if (recommendedSizes?.shopProductBySkuId?.id) {
        recommendedSizesArr = [recommendedSizes];
      }

      return Update(
        {
          ...oldState,
          recommendedSizes: recommendedSizesArr || [],
          recommendedSizesLoading: action.payload.recommendedSizesLoading,
          recommendedSizesError: action.payload.recommendedSizesError,
          recommendedSkuSizeLabel,
          recommendedSkuInseamLabel,
        },
        () => {
          return null;
        },
      );
    case 'SET_SHOP_WAYS_TO_WEAR_IT_QUERY_DATA':
      const waysToWearItCollection =
        action.payload.shopWaysToWearItData?.shopContent?.productDetailsPage
          ?.waysToWearItCollection;

      return UpdateWithShopProductComputedState({
        ...oldState,
        waysToWearIt:
          (waysToWearItCollection as WaysToWearItCollection) || null,
        waysToWearItLoading: action.payload.shopWaysToWearItLoading,
        waysToWearItError: action.payload.shopWaysToWearItError,
      });
    case 'SET_SHOP_MATCHING_SET_QUERY_DATA':
      const matchingSet =
        action.payload.shopMatchingSetData?.shopContent?.productDetailsPage
          ?.matchingSetModule;

      return UpdateWithShopProductComputedState({
        ...oldState,
        matchingSet: (matchingSet as ShopMatchingSetModule) || null,
        matchingSetError: action.payload.shopMatchingSetError,
        matchingSetLoading: action.payload.shopMatchingSetLoading,
      });
    case 'SET_SHOP_RECOMMENDED_COLLECTIONS_QUERY_DATA':
      return UpdateWithShopProductComputedState({
        ...oldState,
        recommendedCollections:
          (action.payload.shopRecommendedCollectionsData?.shopContent
            ?.productDetailsPage
            ?.recommendedShopCollections as ShopProductDiscoverSkusCollection[]) ||
          null,
        recommendedCollectionsLoading:
          action.payload.shopRecommendedCollectionsLoading,
        recommendedCollectionsError:
          action.payload.shopRecommendedCollectionsError,
      });
    case 'SET_SHOP_CATEGORIES_QUERY_DATA':
      return UpdateWithShopProductComputedState({
        ...oldState,
        shopCategories:
          action.payload.shopCategoriesData?.shopContent?.productDetailsPage
            ?.categorySetModule,
        shopCategoriesError: action.payload.shopCategoriesError,
        shopCategoriesLoading: action.payload.shopCategoriesLoading,
      });

    case 'SET_CREDIT_INCENTIVES_ELIGIBILITY_QUERY_DATA':
      const { visitor } = action.payload.creditIncentivesEligibilityData || {};

      const { configParams } = visitor || {};

      return UpdateWithShopProductComputedState({
        ...oldState,
        creditIncentivesEligibility: {
          configParams,
        },
        creditIncentivesEligibilityLoading:
          action.payload.creditIncentivesEligibilityLoading,
        creditIncentivesEligibilityError:
          action.payload.creditIncentivesEligibilityError,
      });

    case 'SET_SHOPPING_ACROSS_BUSINESS_LINES_ELIGIBILITY_QUERY_DATA':
      const { client } =
        action.payload.shoppingAcrossBusinessLinesEligibilityData || {};

      const eligibility = client?.configParams;

      return UpdateWithShopProductComputedState({
        ...oldState,
        shoppingAcrossBusinessLinesEligibility: { eligibility },
        shoppingAcrossBusinessLinesEligibilityLoading:
          action.payload.shoppingAcrossBusinessLinesEligibilityLoading,
        shoppingAcrossBusinessLinesEligibilityError:
          action.payload.shoppingAcrossBusinessLinesEligibilityError,
      });

    case 'WAYS_TO_WEAR_IT_VIEWED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackWaysToWearItCarouselViewed(state);
        },
      );
    case 'WAYS_TO_WEAR_IT_ACCORDION_CHANGED':
      return UpdateWithSideEffect(
        {
          ...oldState,
          expanded: action.payload.expanded,
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackWaysToWearItCarouselAccordionChanged(state);
        },
      );
    case 'WAYS_TO_WEAR_IT_OUTFIT_VIEWED':
      return UpdateWithSideEffect(
        {
          ...oldState,
          outfitId: action.payload.outfitId,
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackWaysToWearItOutfitViewed(state);
        },
      );
    case 'WAYS_TO_WEAR_IT_OUTFIT_ITEM_CLICKED':
      return UpdateWithSideEffect(
        {
          ...oldState,
          clickedSkuId: action.payload.item.id,
          outfitId: action.payload.outfitId,
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackWaysToWearItOutfitItemClicked(state);
        },
      );
    case 'COLOR_SELECTED':
      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          selectedColorLabel: action.payload.colorLabel,
          selectedUniqueColorId: action.payload.uniqueColorId,
          selectedUniqueColorIdHistory: [
            ...oldState.selectedUniqueColorIdHistory,
            ...[action.payload.uniqueColorId || ''],
          ],
          ctaTooltipDisplay: hideCtaTooltips,
          oosData: {
            selectedSkuId: null,
            shopSizeOptions: [],
            tracked: false,
          },
          clientSelectedColor: true,
        },
        (state: ProductDetailsPageUnifiedState) => {
          sendCount('color_selected', {
            gated: state.gated,
          });

          updateStructuredMarkup(state);
        },
      );

    case 'SIZE_SELECTED':
      const nextState = oldState.selectedSizeLabel
        ? {
            ...oldState,
            selectedSizeLabel: action.payload.sizeLabel,
            selectedInseamLabel: null,
            fallbackInseamLabel: null,
            shopInseamOptions: [],
            ctaTooltipDisplay: hideCtaTooltips,
          }
        : {
            ...oldState,
            selectedSizeLabel: action.payload.sizeLabel,
            selectedInseamLabel: null,
            fallbackInseamLabel: null,
            shopInseamOptions: [],
            ctaTooltipDisplay: hideCtaTooltips,
            // If oldstate.selectedSizeLabel is null, then we were in an unselected state.
            // We want to allow the OOS event to fire when clicking a size, so we reset
            // the oosData object in this case.
            oosData: {
              selectedSkuId: null,
              shopSizeOptions: oldState.shopSizeOptions,
              tracked: false,
            },
          };

      return UpdateWithShopProductComputedStateAndSideEffect(
        nextState,
        (state: ProductDetailsPageUnifiedState) => {
          updateStructuredMarkup(state);
        },
      );

    case 'SET_FALLBACK_SIZE':
      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          fallbackSizeLabel: action.payload.sizeLabel,
        },
        (state: ProductDetailsPageUnifiedState) => {
          updateStructuredMarkup(state);
        },
      );

    case 'INSEAM_SELECTED':
      const nextInseamState = oldState.selectedInseamLabel
        ? {
            ...oldState,
            selectedInseamLabel: action.payload.inseamLabel,
            ctaTooltipDisplay: hideCtaTooltips,
          }
        : {
            ...oldState,
            clientSelectedInseam: true,
            selectedInseamLabel: action.payload.inseamLabel,
            ctaTooltipDisplay: hideCtaTooltips,
            // If oldstate.selectedInseamLabel is null, then we were in an unselected state.
            // We want to allow the OOS event to fire when clicking an inseam, so we reset
            // the oosData object in this case.
            oosData: {
              selectedSkuId: null,
              shopSizeOptions: oldState.shopSizeOptions,
              tracked: false,
            },
          };

      return UpdateWithShopProductComputedStateAndSideEffect(
        nextInseamState,
        (state: ProductDetailsPageUnifiedState) => {
          updateStructuredMarkup(state);
        },
      );

    case 'SET_FALLBACK_INSEAM':
      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
          fallbackInseamLabel: action.payload.inseamLabel,
        },
        (state: ProductDetailsPageUnifiedState) => {
          updateStructuredMarkup(state);
        },
      );

    // The below case is used for window popstate events which are triggered by
    // browser actions like clicking the back or forward buttons.
    case 'BROWSER_STATE_POPPED':
      return UpdateWithShopProductComputedState({
        ...action.payload.previousState,
      });
    case 'CLOSE_INVENTORY_FAILURE_ALERT':
      return Update({
        ...oldState,
        showInventoryFailureModal: false,
      });
    case 'CLOSE_RATE_LIMIT_FAILURE_ALERT':
      return Update({
        ...oldState,
        showRateLimitFailureModal: false,
      });
    case 'CLEAR_TOOLTIPS':
      return Update({
        ...oldState,
        ctaTooltipDisplay: hideCtaTooltips,
      });
    case 'SET_SHOPPING_BAG_ERROR':
      return Update({
        ...oldState,
        bagError: action.payload.bagError,
      });
    case 'DISABLED_ADD_TO_BAG_CLICKED':
      return Update({
        ...oldState,
        ctaTooltipDisplay: {
          showBuyNow: false,
          showAddToBag: true,
        },
      });
    case 'DISABLED_BUY_NOW_CLICKED':
      return Update({
        ...oldState,
        ctaTooltipDisplay: {
          showBuyNow: true,
          showAddToBag: false,
        },
      });
    case 'BUY_NOW_CLICKED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackBuyNowClick(state);
          trackGtmBuyNowButtonClick(state);
          redirectToSignup({
            skus: [
              {
                id: state.selectedSkuId,
                quantity: 1,
                source: state.source,
                subSource: state.subSource,
              },
            ],
            productBusinessLine: state.productBusinessLine,
            orderType: 'ungated_buy_now',
            orderSubtotal: state.formattedPrice,
          });
        },
      );
    case 'JUMP_LINK_CLICKED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackSimilarItemsLinkClick(state);
        },
      );
    case 'RECOMMENDED_COLLECTIONS_CAROUSEL_VIEWED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          const carouselMap: {
            [key: string]: string;
          } = {
            best_brands: 'brand-items',
            brand: 'brand-items',
            similar_items: 'similar-items',
          };

          const carouselId =
            carouselMap[action?.payload?.carouselId] ||
            action?.payload?.carouselId ||
            '';

          trackRelatedItemsViewed({
            selectedSkuId: state.selectedSkuId,
            externalProductId: state.externalProductId,
            relatedItems:
              state.recommendedCollections?.find(
                collection =>
                  collection.treatment === action?.payload?.carouselId,
              )?.shopProducts?.nodes || [],
            subSource: carouselId,
            productBusinessLine: state.productBusinessLine,
            clientBusinessLine: state.clientBusinessLine,
            skipEventTracking: state.skipEventTracking,
          });
        },
      );
    case 'RECOMMENDED_COLLECTIONS_CAROUSEL_SCROLLED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          const carouselMap: {
            [key: string]: string;
          } = {
            best_brands: 'brand-items',
            brand: 'brand-items',
            similar_items: 'similar-items',
          };

          const carouselId =
            carouselMap[action?.payload?.carouselId] ||
            action?.payload?.carouselId ||
            '';

          trackFirstRelatedItemsScroll({
            selectedSkuId: state.selectedSkuId,
            externalProductId: state.externalProductId,
            relatedItems:
              state.recommendedCollections?.find(
                collection =>
                  collection.treatment === action?.payload?.carouselId,
              )?.shopProducts?.nodes || [],
            subSource: carouselId,
            productBusinessLine: state.productBusinessLine,
            clientBusinessLine: state.clientBusinessLine,
            skipEventTracking: state.skipEventTracking,
          });
        },
      );
    case 'RECOMMENDED_COLLECTIONS_CAROUSEL_ITEM_CLICKED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          const carouselMap: {
            [key: string]: string;
          } = {
            best_brands: 'brand-items',
            brand: 'brand-items',
            similar_items: 'similar-items',
          };

          const carouselId =
            carouselMap[action?.payload?.carouselId] ||
            action?.payload?.carouselId ||
            '';

          trackRelatedItemClick({
            externalProductId: state.externalProductId,
            relatedItems:
              state.recommendedCollections?.find(
                collection =>
                  collection.treatment === action?.payload?.carouselId,
              )?.shopProducts?.nodes || [],
            subSource: carouselId,
            clickedSku: action.payload.itemId,
            productBusinessLine: state.productBusinessLine,
            clientBusinessLine: state.clientBusinessLine,
            skipEventTracking: state.skipEventTracking,
          });
        },
      );
    case 'RECOMMENDED_COLLECTIONS_CAROUSEL_END_CARD_VIEWED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          const carouselMap: {
            [key: string]: string;
          } = {
            best_brands: 'brand-items',
            brand: 'brand-items',
            similar_items: 'similar-items',
          };

          const carouselId =
            carouselMap[action?.payload?.carouselId] ||
            action?.payload?.carouselId ||
            '';

          trackRelatedItemsEndCardViewed({
            selectedSkuId: state.selectedSkuId,
            externalProductId: state.externalProductId,
            relatedItems:
              state.recommendedCollections?.find(
                collection =>
                  collection.treatment === action?.payload?.carouselId,
              )?.shopProducts?.nodes || [],
            subSource: carouselId,
            productBusinessLine: state.productBusinessLine,
            clientBusinessLine: state.clientBusinessLine,
            skipEventTracking: state.skipEventTracking,
          });
        },
      );
    case 'RECOMMENDED_COLLECTIONS_CAROUSEL_END_CARD_CLICKED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          const carouselMap: {
            [key: string]: string;
          } = {
            best_brands: 'brand-items',
            brand: 'brand-items',
            similar_items: 'similar-items',
          };

          const carouselId =
            carouselMap[action?.payload?.carouselId] ||
            action?.payload?.carouselId ||
            '';

          trackRelatedItemsEndCardClick({
            selectedSkuId: state.selectedSkuId,
            externalProductId: state.externalProductId,
            relatedItems:
              state.recommendedCollections?.find(
                collection =>
                  collection.treatment === action?.payload?.carouselId,
              )?.shopProducts?.nodes || [],
            subSource: carouselId,
            productBusinessLine: state.productBusinessLine,
            clientBusinessLine: state.clientBusinessLine,
            skipEventTracking: state.skipEventTracking,
          });
        },
      );
    case 'RECOMMENDED_COLLECTIONS_CAROUSEL_VIEW_ALL_CLICKED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          const carouselMap: {
            [key: string]: string;
          } = {
            best_brands: 'brand-items',
            brand: 'brand-items',
            similar_items: 'similar-items',
          };

          const carouselId =
            carouselMap[action?.payload?.carouselId] ||
            action?.payload?.carouselId ||
            '';

          trackRelatedItemsViewAllClick({
            selectedSkuId: state.selectedSkuId,
            externalProductId: state.externalProductId,
            relatedItems:
              state.recommendedCollections?.find(
                collection =>
                  collection.treatment === action?.payload?.carouselId,
              )?.shopProducts?.nodes || [],
            subSource: carouselId,
            productBusinessLine: state.productBusinessLine,
            clientBusinessLine: state.clientBusinessLine,
            skipEventTracking: state.skipEventTracking,
          });
        },
      );
    case 'RECOMMENDED_SIZES_TAB_CLICKED':
      const recommendedSize = oldState.recommendedSizes[0];
      const sizeLabel =
        recommendedSize?.shopProductBySkuId?.variantBySkuId?.size?.name || null;
      const inseamLabel =
        recommendedSize?.shopProductBySkuId?.variantBySkuId?.inseam?.name ||
        null;

      return Update(
        {
          ...oldState,
          onRecommendedSizesTab: true,
          selectedSizeLabel: sizeLabel,
          selectedInseamLabel: inseamLabel,
          showStyleProfileCta: false,
        },
        () => {
          return null;
        },
      );
    case 'ALL_SIZES_TAB_CLICKED':
      return UpdateWithSideEffect(
        {
          ...oldState,
          onRecommendedSizesTab: false,
          showStyleProfileCta: true,
        },
        (state: ProductDetailsPageUnifiedState) => {
          sendCount('all_sizes_tab_clicked', {
            gated: state.gated,
          });

          return null;
        },
      );
    case 'UPDATE_OOS_DATA_SIZE_OPTIONS':
      return Update(
        {
          ...oldState,
          oosData: {
            ...oldState.oosData,
            shopSizeOptions: action.payload.shopSizeOptions,
          },
        },
        () => {
          return null;
        },
      );
    case 'UPDATE_OOS_DATA_SELECTED_SKU_ID':
      return Update(
        {
          ...oldState,
          oosData: {
            ...oldState.oosData,
            selectedSkuId: action.payload.selectedSkuId,
          },
        },
        () => {
          return null;
        },
      );
    case 'TRACK_OUT_OF_STOCK':
      return UpdateWithSideEffect(
        {
          ...oldState,
          oosData: {
            ...oldState.oosData,
            tracked: true,
          },
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackInventoryAvailabilityEvent(state);
        },
      );
    case 'NEW_ENTRY_POINTS_CARD_VIEWED':
      return UpdateWithSideEffect(
        {
          ...oldState,
        },
        (state: ProductDetailsPageUnifiedState) => {
          trackNewEntryPointsCardView(state);
        },
      );
    case 'SET_PREVIOUS_USE_SHOP_PRODUCT_QUERY_ARGUMENTS':
      return Update({
        ...oldState,
        previousUseShopProductQueryArguments: {
          productSkuId: action.payload.productSkuId,
          externalProductId: action.payload.externalProductId,
          gated: action.payload.gated,
        },
      });
    default:
      // HACK: doing this just to make the linter happy
      return UpdateWithShopProductComputedStateAndSideEffect(
        {
          ...oldState,
        },
        () => console.log('default side effect test'),
      );
  }
};

export default productDetailsPageContextReducer;
